import { Component, EventEmitter, HostListener, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import {
  RadioGroupComponent,
  RadioComponent,
  RadioModule,
} from "@vg-constellation/angular-17/radio";
import { DropdownMenuItem } from "@vg-constellation/angular-17/dropdown";
import { LinkComponent, LinkModule } from "@vg-constellation/angular-17/link";
import { IconModule } from "@vg-constellation/angular-17/icon";
import { CardComponent } from "@vg-constellation/angular-17/card";

import { BeneficiaryType } from "../../types/BeneficiaryType";
import {
  Beneficiary,
  BeneficiarySectionType,
  EditBeneficiaryPayload,
  EditTrustBeneficiaryPayload,
  getCardDensity,
} from "@models/beneficiary.model";
import { Trust } from "@models/estateProfile.model";
import { Person } from "@services/zok";
import * as content from "@services/content/guided-flow/account-details-content";

import { BENEFICIARY_TYPE_OPTIONS } from "./beneficiary-type.config";
import { PersonInputFormComponent } from "./person-input-form/person-input-form.component";
import { TrustInputFormComponent } from "./trust-input-form/trust-input-form.component";

interface RadioStateChangeEvent {
  inputId?: string;
}

@Component({
  selector: "app-input-beneficiary-card",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RadioGroupComponent,
    RadioComponent,
    RadioModule,
    LinkComponent,
    LinkModule,
    IconModule,
    CardComponent,
    PersonInputFormComponent,
    TrustInputFormComponent,
  ],
  templateUrl: "./input-beneficiary-card.component.html",
  styleUrls: ["./input-beneficiary-card.component.scss"],
})
export class InputBeneficiaryCardComponent implements OnInit {
  @Input() mode: "add" | "edit" = "add";
  @Input() existingData: Beneficiary | null = null;
  @Input() householdMembers: DropdownMenuItem[] = [];
  @Input() householdPeople: Person[] = [];
  @Input() trusts: Trust[] = [];
  @Input() settlors: DropdownMenuItem[] = [];
  @Input() section: BeneficiarySectionType = "PRIMARY";
  @Input() showCancelOnTypeSelection = false;
  @Input() skipTypeSelection = false;
  @Input() isSaving = false;
  @Input() showSelectError = false;

  @Output() save = new EventEmitter<Beneficiary>();
  @Output() saveEdit = new EventEmitter<EditBeneficiaryPayload>();
  @Output() saveEditTrust = new EventEmitter<EditTrustBeneficiaryPayload>();
  @Output() cancel = new EventEmitter<void>();

  protected readonly content = content;
  protected readonly BeneficiaryType = BeneficiaryType;
  readonly typeOptions = BENEFICIARY_TYPE_OPTIONS;

  selectedBeneficiaryType: BeneficiaryType | null = null;
  showTypeSelection = true;
  cardDensity: "small" | "medium" = "medium";

  ngOnInit(): void {
    this.cardDensity = getCardDensity();

    if (this.mode === "edit" && this.existingData) {
      this.selectedBeneficiaryType = this.existingData.beneficiaryType;
      this.showTypeSelection = false;
    } else if (this.skipTypeSelection && this.mode === "add") {
      this.selectedBeneficiaryType = BeneficiaryType.INDIVIDUAL_BY_NAME;
      this.showTypeSelection = false;
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    this.cardDensity = getCardDensity();
  }

  handleTypeSelection(event: RadioStateChangeEvent): void {
    const option = this.typeOptions.find((o) => o.radioId === event.inputId);
    if (option) {
      this.selectedBeneficiaryType = option.type;
      this.showTypeSelection = false;
    }
  }

  handleChangeType(): void {
    this.showTypeSelection = true;
    this.selectedBeneficiaryType = null;
  }

  handleCancel(): void {
    this.cancel.emit();
  }
}
